// ============================================================================
// Time-derived checklist lifecycle status.
// ----------------------------------------------------------------------------
// Computed at read time from `scheduleStartDate` / `scheduleEndDate` plus an
// optional workspace `todosLeadDays` setting. Never persisted — projecting
// this on the client keeps admin lead-time changes instant, avoids midnight
// cron jobs, and matches the existing "download all workspace checklists +
// filter client-side" pattern used by the helper and member feeds.
//
// Semantics:
//   DURATION (both start and end):
//     now < start − leadDays  → 'upcoming'
//     start − leadDays ≤ now ≤ end → 'active'
//     now > end               → 'archived'
//   DATED (single date, or start present with no distinct end date):
//     now ≤ start             → 'active' (lead-time is a no-op here — the
//                                item is already active before its date)
//     now > start             → 'archived'
//   No start date at all      → 'active' (backward compatible: nothing to
//                                derive against, so don't hide anything)
// ============================================================================
import type { ChecklistScheduleMode, ChecklistTimeStatus } from '../types/schema'

export type DeriveTimeStatusInput = {
  scheduleMode?: ChecklistScheduleMode | null
  scheduleStartDate?: string | null
  scheduleEndDate?: string | null
  /** Fallback to legacy `startDate`/`endDate` fields when the schedule*
   *  variants aren't populated. Both member/helper adapters already project
   *  onto scheduleStart/EndDate but occasional writes still miss the newer
   *  fields, so we accept either. */
  startDate?: string | null
  endDate?: string | null
  /** Workspace admin setting. When > 0, DURATION items enter 'active' this
   *  many days before start (i.e. show up in Todos earlier). Default 0. */
  leadDays?: number
}

/** Parse "YYYY-MM-DD" or a full ISO datetime into a Date at local midnight.
 *  Returns null for unusable input so callers can fall back to 'active'. */
const parseDayStart = (value: string | null | undefined): Date | null => {
  if (!value) return null
  const trimmed = value.length >= 10 ? value.slice(0, 10) : value
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (!match) return null
  const [, y, m, d] = match
  const date = new Date(Number(y), Number(m) - 1, Number(d))
  date.setHours(0, 0, 0, 0)
  return date
}

/** Same, but returns end-of-day so an `endDate` compares as inclusive. */
const parseDayEnd = (value: string | null | undefined): Date | null => {
  const start = parseDayStart(value)
  if (!start) return null
  start.setHours(23, 59, 59, 999)
  return start
}

const DAY_MS = 24 * 60 * 60 * 1000

export const hasChecklistDurationRange = (input: Pick<DeriveTimeStatusInput, 'scheduleStartDate' | 'scheduleEndDate' | 'startDate' | 'endDate'>): boolean => {
  const startIso = input.scheduleStartDate ?? input.startDate ?? null
  const endIso = input.scheduleEndDate ?? input.endDate ?? null
  const start = parseDayStart(startIso)
  const end = parseDayStart(endIso)
  return Boolean(start && end && start.getTime() !== end.getTime())
}

export const deriveChecklistTimeStatus = (
  input: DeriveTimeStatusInput,
  now: Date = new Date(),
): ChecklistTimeStatus => {
  const startIso = input.scheduleStartDate ?? input.startDate ?? null
  const endIso = input.scheduleEndDate ?? input.endDate ?? null
  const start = parseDayStart(startIso)
  const end = parseDayEnd(endIso)
  const leadDays = Math.max(0, Math.floor(input.leadDays ?? 0))

  // No usable dates → always active (backward compatible).
  if (!start && !end) return 'active'

  // A real date range wins over scheduleMode. Older callers occasionally
  // persisted DATED with distinct start/end days; read surfaces must still
  // treat that as duration so they do not collapse the range.
  const mode: ChecklistScheduleMode | null | undefined = input.scheduleMode ?? null

  // DURATION path: distinct start/end days OR explicit DURATION with at least start.
  const isDuration = (start !== null && end !== null && start.getTime() !== end.getTime())
    || mode === 'DURATION'

  if (isDuration && start) {
    const activeFrom = new Date(start.getTime() - leadDays * DAY_MS)
    if (now.getTime() < activeFrom.getTime()) return 'upcoming'
    if (end && now.getTime() > end.getTime()) return 'archived'
    return 'active'
  }

  // DATED path (single date): before-or-on = active, after = archived.
  // The reference date is start (fallback to end if only end is present).
  const reference = start ?? end
  if (!reference) return 'active'
  const endOfDay = new Date(reference)
  endOfDay.setHours(23, 59, 59, 999)
  return now.getTime() > endOfDay.getTime() ? 'archived' : 'active'
}
